/* eslint-disable react/jsx-props-no-spreading */
import React, { useMemo } from 'react';
import type { AppProps } from 'next/app';
import { UserProvider } from '@auth0/nextjs-auth0/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@descope/nextjs-sdk';
import { getDescopeConfig } from 'utils/descopeConfig';
import { FeatureFlagsContext } from '@/context/FeatureFlagsContext';
import { ResidentAuthContext } from '@/context/ResidentAuthContext';
import { determineCountryFromProps, determineRegionFromProps } from 'utils/determineRegionFromProps';
import { ToastProvider } from '@/components/Atoms/Toast/ToastProvider';
import { EqualWebAccessibility } from '@/components/Metadata/EqualWebAccessibility';
import '@/styles/globals.css';

// eslint-disable-next-line id-length
export default function App({ Component, pageProps }: AppProps) {
  const queryClient = useMemo(() => new QueryClient({}), []);

  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument
  const country = determineCountryFromProps(pageProps?.serverData);
  const dataCenter = determineRegionFromProps(country);

  // publicEnvVars is injected by getServerSideProps on every page and is the
  // authoritative source for NEXT_PUBLIC_* vars (they are not inlined at build time in CI).
  // _app.tsx renders before setPublicEnvAsGlobal runs, so we pass publicEnvVars directly.
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const { projectId } = getDescopeConfig(dataCenter, pageProps?.publicEnvVars);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
  const pageFeatureFlags: Record<string, boolean> | undefined = pageProps?.featureFlags;
  const featureFlags = useMemo<Record<string, boolean>>(() => pageFeatureFlags ?? {}, [pageFeatureFlags]);

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
  const equalWebSitekey: string | null = pageProps?.publicEnvVars?.NEXT_PUBLIC_EQUALWEB_SITEKEY ?? null;
  const equalWeb = <EqualWebAccessibility country={country} sitekey={equalWebSitekey} />;

  // Feature flags must not depend on descope/country resolution — they gate behavior
  // (e.g. comContentNoIndex) on pages that render before/without auth being configured.
  if (!projectId || !country) {
    return (
      <QueryClientProvider client={queryClient}>
        <FeatureFlagsContext.Provider value={featureFlags}>
          <UserProvider>
            <ToastProvider>
              <Component {...pageProps} />
            </ToastProvider>
          </UserProvider>
          {equalWeb}
        </FeatureFlagsContext.Provider>
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <FeatureFlagsContext.Provider value={featureFlags}>
        <AuthProvider projectId={projectId}>
          <ResidentAuthContext.Provider value>
            <UserProvider>
              <ToastProvider>
                <Component {...pageProps} />
              </ToastProvider>
            </UserProvider>
          </ResidentAuthContext.Provider>
        </AuthProvider>
        {equalWeb}
      </FeatureFlagsContext.Provider>
    </QueryClientProvider>
  );
}
